import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
import { Textarea } from '../ui/Textarea';
import { Input } from '../ui/Input';
import { CheckboxGroup } from '../ui/CheckboxGroup';
import { MascotPanel } from '../ui/MascotPanel';
import { Button } from '../ui/Button';
import { Tooltip } from '../ui/Tooltip';
import { PlusIcon, Trash2Icon, InfoIcon } from 'lucide-react';
export function Step5Story() {
  const {
    data,
    updateData
  } = useIntake();
  const addService = () => {
    const newService = {
      id: Date.now().toString(),
      name: '',
      description: ''
    };
    updateData({
      services: [...data.services, newService]
    });
  };
  const removeService = (id: string) => {
    updateData({
      services: data.services.filter(s => s.id !== id)
    });
  };
  const updateService = (id: string, field: string, value: string) => {
    updateData({
      services: data.services.map(s => s.id === id ? {
        ...s,
        [field]: value
      } : s)
    });
  };
  const feelOptions = [{
    value: 'professional',
    label: 'Professioneel & Zakelijk'
  }, {
    value: 'modern',
    label: 'Modern & Innovatief'
  }, {
    value: 'luxury',
    label: 'Luxe & Premium'
  }, {
    value: 'approachable',
    label: 'Toegankelijk & Vriendelijk'
  }, {
    value: 'expert',
    label: 'Autoriteit & Expert'
  }, {
    value: 'playful',
    label: 'Speels & Creatief'
  }, {
    value: 'calm',
    label: 'Rustig & Betrouwbaar'
  }, {
    value: 'bold',
    label: 'Stoer & Opvallend'
  }, {
    value: 'other',
    label: 'Anders...'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="max-w-3xl mx-auto space-y-8" data-id="element-471" data-mp="6goo8r12fjow">
      <MascotPanel message="Nu wordt het leuk! Vertel me meer over het DNA van uw bedrijf. Wat maakt u uniek? Deze input is cruciaal voor het design en de teksten." data-id="element-472" data-mp="3hunq7k4s1hpi" />

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 space-y-8" data-id="element-473" data-mp="3rtn0f756xlem">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-474" data-mp="3mvbs50ujniyi">
          Bedrijfsverhaal & Positionering
        </h2>

        <div data-id="element-475" data-mp="2z6siohzrd04u">
          <div className="flex items-center mb-3" data-id="element-476" data-mp="2z6iosjble8tv">
            <label className="block text-sm font-semibold text-gray-900" data-id="element-477" data-mp="2u761xykq11ra">
              Uw diensten of producten
            </label>
            <Tooltip content="Voeg uw belangrijkste diensten of producten toe. Dit helpt ons de juiste paginastructuur, content en SEO silo's te bepalen." data-id="element-478" data-mp="38wlv1l7q5ja5">
              <button type="button" className="ml-2 text-gray-400 hover:text-[#007AFF] focus:outline-none" data-id="element-479" data-mp="2gf9h8fldtu4x">
                <InfoIcon className="w-4 h-4" data-id="element-480" data-mp="2bfws1o71gaz6" />
              </button>
            </Tooltip>
          </div>

          <div className="space-y-4" data-id="element-481" data-mp="qx2ugov3qeo7">
            {data.services.map((service, index) => <motion.div key={service.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 relative" data-id="element-482" data-mp="vqf5fw6uymyh" data-mpi={index}>
                <button onClick={() => removeService(service.id)} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" data-id="element-483" data-mp="1eo89j1t7x2xg">
                  <Trash2Icon className="w-4 h-4" data-id="element-484" data-mp="14nzalg7ud3ed" />
                </button>

                <h4 className="font-semibold text-gray-900 mb-3 text-sm" data-id="element-485" data-mp="3ldmvk691x02c">
                  Dienst {index + 1}
                </h4>

                <div className="space-y-3" data-id="element-486" data-mp="3pdx1xk0r9shq">
                  <Input label="Naam dienst / product" placeholder="Bijv. Belastingadvies, Loodgieter spoedservice, Webdesign..." value={service.name} onChange={e => updateService(service.id, 'name', e.target.value)} data-id="element-487" data-mp="9i1ksdmov0h5" />
                  <Textarea label="Korte beschrijving" placeholder="Wat houdt deze dienst in? Voor wie is het? Wat maakt het bijzonder?" value={service.description} onChange={e => updateService(service.id, 'description', e.target.value)} className="min-h-[80px]" data-id="element-488" data-mp="3qwf6qrxkkp4h" />
                </div>
              </motion.div>)}

            <Button variant="outline" onClick={addService} className="w-full border-dashed" data-id="element-489" data-mp="336kuocl2r4la">
              <PlusIcon className="w-5 h-5 mr-2" data-id="element-490" data-mp="1epuhhw52z5wo" />
              Dienst / Product toevoegen
            </Button>
          </div>
        </div>

        <Textarea label="Wat maakt u anders dan concurrenten?" infoTooltip="Denk aan uw Unique Selling Points (USP's). Waarom kiezen klanten voor u en niet voor de buurman?" placeholder="Bijv. Wij bieden 24/7 service, hebben 20 jaar ervaring, of gebruiken een unieke methode..." value={data.difference} onChange={e => updateData({
        difference: e.target.value
      })} data-id="element-491" data-mp="1iok0q5csvho8" />

        <Textarea label="Wat zijn uw kernwaarden?" placeholder="Bijv. Transparant, Duurzaam, Klantgericht, Snel..." value={data.coreValues} onChange={e => updateData({
        coreValues: e.target.value
      })} data-id="element-492" data-mp="26hinm0x9mw72" />

        <div data-id="element-493" data-mp="h1t0xxfhgvsd">
          <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-494" data-mp="h1j8m36yucji">
            Hoe moet het bedrijf aanvoelen voor een bezoeker?
            <span className="text-gray-400 font-normal ml-1" data-id="element-495" data-mp="1fddfmk4mic8c">(max 3)</span>
          </label>
          <CheckboxGroup columns={2} options={feelOptions} values={data.companyFeel} onChange={vals => {
          if (vals.length <= 3) updateData({
            companyFeel: vals
          });
        }} data-id="element-496" data-mp="1tcmvfiox34bs" />

          <AnimatePresence data-id="element-497" data-mp="1yc7stoofv4me">
            {data.companyFeel.includes('other') && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="mt-4 overflow-hidden" data-id="element-498" data-mp="2c3cz39mbav5p">
                <Input placeholder="Vul hier in hoe het bedrijf moet aanvoelen..." value={data.companyFeelOther} onChange={e => updateData({
              companyFeelOther: e.target.value
            })} data-id="element-499" data-mp="30oiqcanbtqz7" />
              </motion.div>}
          </AnimatePresence>
        </div>

        <Textarea label="Zijn er woorden of thema's die we absoluut moeten vermijden?" placeholder="Bijv. Gebruik het woord 'goedkoop' niet, wij zijn 'voordelig'." value={data.mustAvoid} onChange={e => updateData({
        mustAvoid: e.target.value
      })} data-id="element-500" data-mp="2hx80zxx91m9f" />

        <div className="pt-6 border-t border-gray-100" data-id="element-501" data-mp="2cyy4njzsgfm8">
          <Textarea label="Beschrijf uw ideale website in 2-3 zinnen" infoTooltip="Dit helpt ons (en onze AI) om precies te begrijpen welke richting u op wilt met het design en de sfeer van de website." placeholder="Bijv. Een strakke, moderne site die vertrouwen wekt en bezoekers snel naar het contactformulier leidt..." value={data.idealWebsiteDescription} onChange={e => updateData({
          idealWebsiteDescription: e.target.value
        })} data-id="element-502" data-mp="1yyndefukgz0j" />
        </div>
      </div>
    </motion.div>;
}